import { useState, useEffect, MouseEvent } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { OutlinedInputProps } from "@mui/material/OutlinedInput";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import { EmtIcon } from "./EmtIcon";

export type EmtTextFieldProps = TextFieldProps & {
  /**
   * Set type password for the field
   * @default false
   */
  password?: boolean;
  nonce?: string;
  maxLength?: number;
  /**
   * Open the browser's picker when a date/time field is pressed. Disable it
   * to render a custom picker instead.
   * @default true
   */
  nativePicker?: boolean;
};

// Opens the native picker wherever the press lands on the field, and keeps the
// input focused so the date can be typed once the picker is closed.
const openNativePicker = (e: MouseEvent<HTMLInputElement>) => {
  const input = e.currentTarget;
  if (input.disabled || input.readOnly) return;
  // Handle focus ourselves so the default mousedown can't close the picker
  e.preventDefault();
  input.focus();
  try {
    input.showPicker?.();
  } catch {
    // showPicker can throw (e.g. cross-origin iframe): the input stays focused
  }
};

export function EmtTextField(props: EmtTextFieldProps) {
  const {
    variant = "filled",
    InputProps,
    helperText,
    type,
    password,
    nativePicker = true,
    ...rest
  } = props;

  const [newType, setNewType] = useState(type);
  const isControlled = props.value !== undefined;

  const isStripeElement =
    typeof InputProps?.inputComponent === "function" &&
    InputProps?.inputComponent.name?.includes("Stripe");

  const [value, setValue] = useState<unknown>(props.value ?? "");

  useEffect(() => {
    if (isControlled) {
      setValue(props.value);
    }
  }, [props.value]);

  const textFieldInputProps: Partial<OutlinedInputProps> = {
    ...(!isStripeElement && {
      inputProps: {
        ...(props.inputProps || {}),
        ...(props.maxLength && { maxLength: props.maxLength }),
        ...(nativePicker &&
          (type === "date" || type === "time" || type === "datetime-local") && {
          onMouseDown: (e: MouseEvent<HTMLInputElement>) => {
            openNativePicker(e);
            props.inputProps?.onMouseDown?.(e);
          },
        }),
        ...(type === "date" && {
          "data-force-mobile": "true",
          // Tells Dashlane not to show its autofill popup on date fields
          "data-form-type": "other",
          autoComplete: "off",
          pattern: "\\d{4}-\\d{2}-\\d{2}",
          placeholder: "YYYY-MM-DD",
        }),
      },
    }),
    ...InputProps,
    endAdornment: (
      <>
        {props.maxLength && (
          <InputAdornment position="end">
            <span style={{ fontSize: 12, opacity: 0.6 }}>
              {String(value ?? "").length}/{props.maxLength}
            </span>
          </InputAdornment>
        )}

        {type === "password" || password ? (
          <InputAdornment position="end">
            <EmtIcon
              className={newType === "password" ? "emt-eye-closed" : "emt-eye"}
              sx={{ cursor: "pointer" }}
              onClick={() =>
                setNewType((prev) =>
                  prev === "password" ? "text" : "password"
                )
              }
            />
          </InputAdornment>
        ) : type === "search" ? (
          <InputAdornment position="end">
            <EmtIcon className={"emt-search-1"} />
          </InputAdornment>
        ) : (
          InputProps?.endAdornment
        )}
      </>
    ),
  };

  return (
    <FormControl
      error={props.error}
      fullWidth={props.fullWidth}
      sx={
        type === "date" || type === "time" || type === "datetime-local"
          ? {
              "& input": { cursor: "pointer" },
              // The icon no longer opens the picker on its own: clicks go
              // through to the input, which handles the whole field
              "& input::-webkit-calendar-picker-indicator": {
                pointerEvents: "none",
              },
            }
          : undefined
      }
    >
      <TextField
        InputProps={textFieldInputProps}
        {...rest}
        variant={variant}
        type={newType}
        value={value}
        onChange={(e) => {
          setValue(e.target?.value);
          props.onChange ? props.onChange(e) : null;
        }}
        className={`${value && value !== props.value ? "no-empty" : ""} ${
          type === "date" ? "date-type" : ""
        } ${"select-icon"}`}
      />

      <FormHelperText
        color={props.color}
        sx={{
          fontFamily: "Nunito Sans",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
      >
        {helperText}
      </FormHelperText>
    </FormControl>
  );
}
